// ============================================================
//  ContestPanel.js — Contact Saver → Referral contest
//  Each participant gets their own WhatsApp link (cmdkit.im/r/CODE). When a NEW person
//  messages the linked number through it, the Contact Saver check spots the code, counts it
//  for that participant and saves the friend with the contest's prefix.
// ============================================================
import './ContestPanel.css';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getContests, searchContest, exportContest, contestAction } from './contestApi';

const errMsg = (e, d) => e?.response?.data?.message || d;
const ago = secs => {
  if (secs == null) return '';
  const m = Math.floor(secs / 60);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  const d = Math.round(m / 1440);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};
const fmtDay = s => (s ? new Date(`${s}T12:00:00`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const fmtUntil = s => (s ? new Date(String(s).replace(' ', 'T')).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '');
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = n => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const copy = (t, msg = 'Copied') => { try { navigator.clipboard.writeText(t); toast.success(msg); } catch { toast.error('Copy it manually'); } };
const naira = n => `₦${Number(n || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;
const PLACE = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];
const prizeList = c => (c?.prizes?.length ? c.prizes : c?.prize ? [c.prize] : []);
const STATE = { live: ['Live', 'live'], scheduled: ['Starts soon', 'soon'], paused: ['Paused', 'paused'], ended: ['Ended', 'ended'] };

export default function ContestPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState('');
  const [q, setQ] = useState('');
  const [found, setFound] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async id => {
    try { const r = await getContests(id); setData(r.data); setErr(''); }
    catch (e) { setErr(errMsg(e, 'Could not load contests')); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // live feed: refresh every minute while the tab is open
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden && data?.contest) load(data.contest.id); }, 60000);
    return () => clearInterval(t);
  }, [data?.contest, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const setNumbers = numbers => setData(d => ({ ...d, numbers, contest: d.contest && { ...d.contest, number: d.contest.number && { ...d.contest.number, ...(numbers.find(n => n.id === d.contest.number.id) || {}) } } }));
  const apply = r => setData(d => ({ ...d, ...r.data, contests: r.data.contest ? mergeList(d.contests, r.data.contest) : d.contests }));
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(errMsg(e, 'Something went wrong')); } setBusy(''); };

  if (err && !data) return <div className="csv-empty"><p>{err}</p><button type="button" className="csv-btn csv-btn-dark" onClick={() => load()}>Try again</button></div>;
  if (!data) return <div className="csv-loading"><span className="csv-spin" /> Loading…</div>;

  const c = data.contest;
  const linkedNumbers = data.numbers.filter(n => n.linked);
  const blank = { id: 0, name: '', prizes: ['', '', ''], accId: (linkedNumbers[0] || data.numbers[0] || {}).id, startsOn: today(), endsOn: plusDays(30),
    template: data.defaultTemplate, prefix: '', openJoin: true, showBoard: true };

  if (!c) {
    return (
      <>
        <section className="csv-card csv-hero rc-empty">
          <div className="csv-hero-ic"><i className="fa-solid fa-trophy" /></div>
          <div className="csv-hero-body">
            <h2>Run a WhatsApp referral contest</h2>
            <p>Every participant gets their own WhatsApp link to your number. Each new person who messages you through it counts for them — and is saved to your contacts automatically.</p>
            <ul className="csv-ticks">
              <li><i className="fa-solid fa-check" />Only brand-new contacts count — no cheating with old customers</li>
              <li><i className="fa-solid fa-check" />Live leaderboard for you and your participants</li>
              <li><i className="fa-solid fa-check" />Uses your linked WhatsApp — {naira(data.price)} a month per number, from your wallet</li>
            </ul>
          </div>
          <button type="button" className="csv-btn csv-btn-brand" onClick={() => setEditing(blank)} disabled={!linkedNumbers.length}><i className="fa-solid fa-plus" /> Start a contest</button>
          {!linkedNumbers.length && <p className="csv-muted rc-wide">Link a WhatsApp number on the <b>Saving contacts</b> tab first — friends message that number.</p>}
        </section>
        {linkedNumbers.length > 0 && <Plans numbers={linkedNumbers} price={data.price} onNumbers={setNumbers} />}
        {editing && <Editor initial={editing} numbers={data.numbers} price={data.price} onClose={() => setEditing(null)} onSaved={r => { apply(r); setEditing(null); }} />}
      </>
    );
  }

  const [stLabel, stTone] = STATE[c.state] || [c.state, 'paused'];
  const board = found ?? data.board;
  const ends = c.state === 'scheduled' ? `Starts ${fmtDay(c.startsOn)}` : c.state === 'ended' ? `Ended ${fmtDay(c.endsOn)}` : `Ends ${fmtDay(c.endsOn)} · ${c.daysLeft === 0 ? 'last day' : `${c.daysLeft} day${c.daysLeft === 1 ? '' : 's'} left`}`;
  const prizes = prizeList(c);
  const savePrefix = c.prefix || c.number?.savePrefix || '';
  const shareText = `Join the ${c.name}${prizes[0] ? ` — 1st prize: ${prizes[0]}` : ''}. Get your link here: ${c.joinUrl}`;

  async function search(e) {
    e.preventDefault();
    if (!q.trim()) { setFound(null); return; }
    run('search', async () => { const r = await searchContest(c.id, q.trim()); setFound(r.data.board); });
  }
  const exportCsv = () => run('export', async () => {
    const r = await exportContest(c.id);
    const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
    const el = document.createElement('a'); el.href = url; el.download = `${c.slug}-participants.csv`; el.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  });
  const setStatus = s => run('status', async () => { const r = await contestAction('status', { id: c.id, status: s }); apply(r); toast.success(r.data.message); });
  const toggleRef = f => run('ref' + f.id, async () => { const r = await contestAction('referral', { id: c.id, rId: f.id, count: !f.counted }); apply(r); toast.success(r.data.message); });

  return (
    <div className="rc">
      {data.contests.length > 1 && (
        <label className="rc-pick">Contest
          <select value={c.id} onChange={e => { setFound(null); setQ(''); load(Number(e.target.value)); }}>
            {data.contests.map(x => <option key={x.id} value={x.id}>{x.name} · {(STATE[x.state] || [x.state])[0]}</option>)}
          </select>
        </label>
      )}

      <section className="rc-hero" aria-labelledby="rc-name">
        <div className="rc-hero-main">
          <div className="rc-tags"><span className={`rc-tag is-${stTone}`}>{stTone === 'live' ? '● ' : ''}{stLabel}</span><span className="rc-tag is-dim">{ends}</span></div>
          <h2 id="rc-name">{c.name}</h2>
          <p className="rc-sub">Friends message {c.number?.label || 'your number'}{c.number?.phone ? ` (${c.number.phone})` : ''} · saved as “{savePrefix ? `${savePrefix} ` : ''}Ada O.”</p>
          {prizes.length > 0 ? (
            <div className="rc-pod">
              {prizes.slice(0, 3).map((p, i) => <div key={i} className={`rc-pz is-${i + 1}`}><span>{PLACE[i]}</span><b>{p}</b></div>)}
              {prizes.length > 3 && <div className="rc-pz is-more"><span>+{prizes.length - 3} more</span><b>{prizes.slice(3).map((p, i) => `${PLACE[i + 3]}: ${p}`).join(' · ')}</b></div>}
            </div>
          ) : <button type="button" className="rc-addpz" onClick={() => setEditing({ ...c })}><i className="fa-solid fa-gift" /> Add prizes — they’re the first thing people see</button>}
        </div>
        <div className="rc-hero-share">
          <label htmlFor="rc-join">Share this so people can join</label>
          <div className="rc-copy"><input id="rc-join" readOnly value={c.joinUrl.replace(/^https?:\/\//, '')} onFocus={e => e.target.select()} />
            <button type="button" className="csv-btn csv-btn-brand" onClick={() => copy(c.joinUrl, 'Join link copied')}>Copy</button></div>
          <div className="rc-hero-btns">
            <a className="rc-ghost" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer"><i className="fa-brands fa-whatsapp" /> Share</a>
            <button type="button" className="rc-ghost" onClick={() => setEditing({ ...c })}><i className="fa-solid fa-pen" /> Edit</button>
            {c.state === 'live' || c.state === 'scheduled'
              ? <button type="button" className="rc-ghost" onClick={() => setStatus('paused')} disabled={busy === 'status'}><i className="fa-solid fa-pause" /> Pause</button>
              : c.status !== 'ended' && c.daysLeft >= 0 && <button type="button" className="rc-ghost" onClick={() => setStatus('live')} disabled={busy === 'status'}><i className="fa-solid fa-play" /> Resume</button>}
            <button type="button" className="rc-ghost" onClick={() => setEditing(blank)}><i className="fa-solid fa-plus" /> New contest</button>
          </div>
        </div>
      </section>

      {c.number && !c.number.linked && (
        <div className="csv-tip rc-warn"><i className="fa-solid fa-triangle-exclamation" />
          <span><b>Referrals aren’t being counted right now</b> — {c.number.label} isn’t linked. Link it again on the <b>Saving contacts</b> tab.</span></div>
      )}
      {c.number && <Plans numbers={[c.number]} price={data.price} onNumbers={setNumbers} />}

      <div className="csv-stats">
        <div className="csv-stat"><span>Participants</span><b>{data.stats.participants}</b><small className="rc-up">{data.stats.participantsToday ? `+${data.stats.participantsToday} today` : 'none today'}</small></div>
        <div className="csv-stat"><span>Referrals counted</span><b>{data.stats.counted}</b><small className="rc-up">{data.stats.countedToday ? `+${data.stats.countedToday} today` : 'none today'}</small></div>
        <div className="csv-stat"><span>New contacts saved</span><b>{data.stats.counted}</b><small>{savePrefix ? `as “${savePrefix} name”` : 'with their WhatsApp name'}</small></div>
        <div className="csv-stat"><span>Not counted</span><b>{data.stats.rejected}</b><small>already saved, self, or removed</small></div>
      </div>

      <div className="rc-cols">
        <section className="csv-card rc-board" aria-labelledby="rc-lb">
          <div className="csv-card-head">
            <h2 id="rc-lb">Leaderboard</h2>
            <div className="rc-tools">
              <form onSubmit={search} className="rc-search" role="search">
                <label className="csv-sr" htmlFor="rc-q">Search participants</label>
                <input id="rc-q" value={q} onChange={e => { setQ(e.target.value); if (!e.target.value) setFound(null); }} placeholder="Name, number or code" />
              </form>
              <button type="button" className="csv-btn csv-btn-ghost csv-btn-sm" onClick={() => setAdding(true)}><i className="fa-solid fa-user-plus" /> Add</button>
              <button type="button" className="csv-btn csv-btn-ghost csv-btn-sm" onClick={exportCsv} disabled={busy === 'export' || !data.stats.participants}><i className="fa-solid fa-file-arrow-down" /> CSV</button>
            </div>
          </div>
          {board.length === 0 ? (
            <p className="csv-muted">{found ? 'Nobody matches that.' : 'No participants yet. Share the join link — people get their own WhatsApp link there.'}</p>
          ) : (
            <div className="csv-table-wrap">
              <table className="csv-table rc-table">
                <thead><tr><th>#</th><th>Participant</th><th>Code</th><th className="rc-r">Friends</th><th className="rc-r">Today</th><th><span className="csv-sr">Link</span></th></tr></thead>
                <tbody>{board.map(p => (
                  <tr key={p.id}>
                    <td><span className={`rc-rank ${p.rank <= 3 ? `is-${p.rank}` : ''}`}>{p.rank}</span></td>
                    <td><b>{p.name}</b><small className="rc-block">{p.phone}</small></td>
                    <td className="csv-mono">{p.code}</td>
                    <td className="rc-r"><b>{p.count}</b></td>
                    <td className={`rc-r ${p.today ? 'rc-up' : 'rc-dim'}`}>+{p.today}</td>
                    <td><button type="button" className="rc-icon" aria-label={`Copy ${p.name}'s link`} onClick={() => copy(p.link, `${p.name.split(' ')[0]}’s link copied`)}><i className="fa-regular fa-copy" /></button></td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}
          {!found && data.stats.participants > board.length && <p className="csv-muted rc-small">Showing the top {board.length} of {data.stats.participants}. Search or export to see everyone.</p>}
        </section>

        <section className="csv-card rc-feed" aria-labelledby="rc-feed">
          <div className="csv-card-head"><h2 id="rc-feed">Live referrals</h2><small className="rc-dim">checked every 5 min</small></div>
          {data.feed.length === 0 ? <p className="csv-muted">New friends show up here within about 5 minutes of messaging you.</p> : (
            <ul className="rc-list">
              {data.feed.map(f => (
                <li key={f.id} className={f.counted ? '' : 'is-off'}>
                  <span className="rc-dot" aria-hidden="true" />
                  <span className="rc-li-body"><span><b>{f.friend}</b> {f.counted ? 'joined via' : 'used the code of'} <b>{f.by}</b></span>
                    <small>{f.counted ? `Counted · ${ago(f.ago)}` : `Not counted — ${f.reason} · ${ago(f.ago)}`}</small></span>
                  <button type="button" className="rc-mini" onClick={() => toggleRef(f)} disabled={busy === 'ref' + f.id}>{f.counted ? 'Remove' : 'Count it'}</button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="csv-card" aria-labelledby="rc-rules">
        <h2 id="rc-rules">When a referral counts</h2>
        <div className="rc-rules">
          <div><b>New number only</b>The friend isn’t already in your phone contacts and hasn’t been saved by Contact Saver before.</div>
          <div><b>Once per friend</b>A number counts for the first code it sends. Sending again, or another code, adds nothing.</div>
          <div><b>No self-referrals</b>A participant’s own number — or another participant’s — never counts.</div>
          <div><b>While it’s live</b>Only between the start and end date, and not while paused. You can remove any referral.</div>
        </div>
      </section>

      {editing && <Editor initial={editing} numbers={data.numbers} price={data.price} onClose={() => setEditing(null)}
        onSaved={r => { apply(r); setEditing(null); }} onDeleted={() => { setEditing(null); setData(null); load(); }} />}
      {adding && <AddParticipant contest={c} onClose={() => setAdding(false)} onAdded={r => { apply(r); setAdding(false); }} />}
    </div>
  );
}

function mergeList(list, c) {
  const row = { id: c.id, name: c.name, state: c.state };
  return list.some(x => x.id === c.id) ? list.map(x => (x.id === c.id ? row : x)) : [row, ...list];
}

function Editor({ initial, numbers, price, onClose, onSaved, onDeleted }) {
  const start = prizeList(initial);
  const [f, setF] = useState({ ...initial, prizes: start.length ? start : ['', '', ''], prefix: initial.prefix || '' });
  const setPrize = (i, v) => setF(x => ({ ...x, prizes: x.prizes.map((p, j) => (j === i ? v : p)) }));
  const [busy, setBusy] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const num = numbers.find(n => n.id === Number(f.accId));
  const sample = (f.template || '').replace('{contest}', f.name || 'your contest').replace('{name}', 'Tolu').replace('{code}', 'TOLU7K');
  async function save() {
    setBusy('save');
    try {
      const r = await contestAction('save', { contest: { id: f.id || undefined, name: f.name, prizes: f.prizes.map(p => p.trim()).filter(Boolean), accId: Number(f.accId), startsOn: f.startsOn, endsOn: f.endsOn,
        template: f.template, prefix: f.prefix, openJoin: f.openJoin, showBoard: f.showBoard } });
      toast.success(r.data.message); onSaved(r);
    } catch (e) { toast.error(errMsg(e, 'Could not save')); }
    setBusy('');
  }
  async function del() {
    if (!confirmDel) { setConfirmDel(true); return; }
    setBusy('del');
    try { const r = await contestAction('delete', { id: f.id }); toast.success(r.data.message); onDeleted(); }
    catch (e) { toast.error(errMsg(e, 'Could not delete')); setBusy(''); }
  }
  return (
    <div className="csv-modal" role="dialog" aria-modal="true" aria-labelledby="rc-ed-t" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="csv-modal-box rc-ed">
        <div className="csv-modal-head">
          <div><h2 id="rc-ed-t">{f.id ? 'Edit contest' : 'Start a referral contest'}</h2>
            {!f.id && <p className="csv-muted">Every participant gets their own WhatsApp link to your number. New people who message you through it count for them.</p>}</div>
          <button type="button" className="csv-x" aria-label="Close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
        </div>
        <label className="csv-field">Contest name<input value={f.name} maxLength={80} onChange={e => set('name', e.target.value)} placeholder="e.g. November Giveaway" autoFocus /></label>
        <fieldset className="rc-prizes">
          <legend>Prizes <small>· shown big on the join page</small></legend>
          {f.prizes.map((p, i) => (
            <div key={i} className="rc-prow">
              <span className={`rc-rank ${i < 3 ? `is-${i + 1}` : ''}`}>{i + 1}</span>
              <label className="csv-sr" htmlFor={`rc-pz-${i}`}>{PLACE[i]} prize</label>
              <input id={`rc-pz-${i}`} value={p} maxLength={120} onChange={e => setPrize(i, e.target.value)}
                placeholder={['₦50,000 cash', '₦20,000 cash', '10GB data'][i] || 'Prize'} />
              {f.prizes.length > 1 && <button type="button" className="rc-icon" aria-label={`Remove ${PLACE[i]} prize`} onClick={() => setF(x => ({ ...x, prizes: x.prizes.filter((_, j) => j !== i) }))}><i className="fa-solid fa-xmark" /></button>}
            </div>
          ))}
          {f.prizes.length < 10 && <button type="button" className="csv-btn csv-btn-ghost csv-btn-sm rc-addrow" onClick={() => setF(x => ({ ...x, prizes: [...x.prizes, ''] }))}><i className="fa-solid fa-plus" /> Add {PLACE[f.prizes.length]} place</button>}
        </fieldset>
        <div className="rc-grid2">
          <label className="csv-field">Starts<input type="date" value={f.startsOn} onChange={e => set('startsOn', e.target.value)} /></label>
          <label className="csv-field">Ends<input type="date" value={f.endsOn} min={f.startsOn} onChange={e => set('endsOn', e.target.value)} /></label>
        </div>
        <label className="csv-field">Which number do friends message?
          <select value={f.accId} onChange={e => set('accId', e.target.value)}>
            {numbers.map(n => <option key={n.id} value={n.id} disabled={!n.linked}>{n.label}{n.phone ? ` · ${n.phone}` : ''}{n.linked ? (n.paid ? ` · contests on till ${fmtUntil(n.until)}` : ' · contests not paid') : ' (not linked)'}</option>)}
          </select>
          {num && num.linked && !num.paid && <small className="rc-warn-t">Referral contests cost {naira(price)} a month on this number. Pay from your wallet on the contest page — referrals only count while it’s paid.</small>}
        </label>
        <label className="csv-field">Message the friend’s WhatsApp is filled with
          <textarea value={f.template} maxLength={400} onChange={e => set('template', e.target.value)} />
          <small>Use <b>{'{name}'}</b> (participant’s first name), <b>{'{contest}'}</b> and <b>{'{code}'}</b>. Keep <b>{'{code}'}</b> — that’s how we know who referred them.</small>
        </label>
        <div className="rc-sample"><span>Example for Tolu</span><p>{sample}</p><small>Short link people share: cmdkit.im/r/TOLU7K</small></div>
        <label className="csv-field">Save new friends with this prefix <small>· empty = your usual Contact Saver prefix</small>
          <input value={f.prefix} maxLength={24} onChange={e => set('prefix', e.target.value)} placeholder="e.g. Promo ·" /></label>
        <div className="rc-toggles">
          <label className="csv-row"><span><b>Anyone can join</b><small>Off: only people you add yourself</small></span>
            <button type="button" role="switch" aria-checked={f.openJoin} aria-label="Anyone can join" className={`csv-switch ${f.openJoin ? 'is-on' : ''}`} onClick={() => set('openJoin', !f.openJoin)}><span /></button></label>
          <label className="csv-row"><span><b>Show the leaderboard</b><small>Participants see the top 10 (names shortened)</small></span>
            <button type="button" role="switch" aria-checked={f.showBoard} aria-label="Show the leaderboard" className={`csv-switch ${f.showBoard ? 'is-on' : ''}`} onClick={() => set('showBoard', !f.showBoard)}><span /></button></label>
        </div>
        <div className="rc-foot">
          {f.id ? <button type="button" className={`csv-btn csv-btn-sm ${confirmDel ? 'csv-btn-dangerfill' : 'csv-btn-danger'}`} onBlur={() => setConfirmDel(false)} onClick={del} disabled={busy !== ''}>{confirmDel ? 'Tap again — deletes everything' : 'Delete contest'}</button> : <span />}
          <div className="rc-foot-r">
            <button type="button" className="csv-btn csv-btn-ghost" onClick={onClose}>Cancel</button>
            <button type="button" className="csv-btn csv-btn-dark" onClick={save} disabled={busy !== '' || f.name.trim().length < 3}>{busy === 'save' ? <span className="csv-spin" /> : null}{f.id ? 'Save' : 'Create contest'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddParticipant({ contest, onClose, onAdded }) {
  const [f, setF] = useState({ name: '', phone: '' });
  const [busy, setBusy] = useState(false);
  async function add(e) {
    e.preventDefault(); setBusy(true);
    try { const r = await contestAction('add', { id: contest.id, ...f }); toast.success(r.data.message); onAdded(r); }
    catch (er) { toast.error(errMsg(er, 'Could not add')); }
    setBusy(false);
  }
  return (
    <div className="csv-modal" role="dialog" aria-modal="true" aria-labelledby="rc-add-t" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="csv-modal-box rc-add" onSubmit={add}>
        <div className="csv-modal-head"><h2 id="rc-add-t">Add a participant</h2>
          <button type="button" className="csv-x" aria-label="Close" onClick={onClose}><i className="fa-solid fa-xmark" /></button></div>
        <label className="csv-field">Name<input value={f.name} maxLength={60} onChange={e => setF(x => ({ ...x, name: e.target.value }))} autoFocus /></label>
        <label className="csv-field">WhatsApp number<input inputMode="tel" value={f.phone} onChange={e => setF(x => ({ ...x, phone: e.target.value }))} placeholder="0803 123 4567" /></label>
        <button type="submit" className="csv-btn csv-btn-dark" disabled={busy || !f.name.trim() || !f.phone.trim()}>{busy ? <span className="csv-spin" /> : null}Add</button>
      </form>
    </div>
  );
}

/** Referral Contest add-on, per number: price, paid-until, two-tap pay from wallet, auto-renew. */
function Plans({ numbers, price, onNumbers }) {
  const [confirm, setConfirm] = useState(null); // { id, nonce }
  const [busy, setBusy] = useState('');
  const [short, setShort] = useState('');
  async function pay(n) {
    if (!confirm || confirm.id !== n.id) { setConfirm({ id: n.id, nonce: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}` }); setShort(''); return; }
    setBusy('pay' + n.id);
    try { const r = await contestAction('pay', { accId: n.id, nonce: confirm.nonce }); onNumbers(r.data.numbers); toast.success(r.data.message); }
    catch (e) {
      const d = e?.response?.data || {};
      if (d.code === 'insufficient') setShort(d.message); else toast.error(d.message || 'Payment failed — nothing was charged');
    }
    setConfirm(null); setBusy('');
  }
  async function renew(n) {
    setBusy('ar' + n.id);
    try { const r = await contestAction('autorenew', { accId: n.id, on: !n.autoRenew }); onNumbers(r.data.numbers); toast.success(r.data.message); }
    catch (e) { toast.error(errMsg(e, 'Could not change auto-renew')); }
    setBusy('');
  }
  return (
    <section className="csv-card rc-plan" aria-labelledby="rc-plan-t">
      <div className="rc-plan-head">
        <div><h2 id="rc-plan-t">Referral Contest plan</h2>
          <p className="csv-muted">Paid per number, monthly, from your wallet. It’s separate from Contact Saver — a contest runs even if Contact Saver is off.</p></div>
        <div className="rc-price"><b>{naira(price)}</b><span>/ month</span></div>
      </div>
      {numbers.map(n => {
        const soon = n.paid && n.daysLeft != null && n.daysLeft <= 3;
        return (
          <div key={n.id} className={`rc-plan-row ${n.paid ? (soon ? 'is-soon' : 'is-on') : 'is-off'}`}>
            <span className="rc-plan-ic" aria-hidden="true"><i className={`fa-solid ${n.paid ? 'fa-circle-check' : 'fa-circle-exclamation'}`} /></span>
            <div className="rc-plan-body">
              <b>{n.label}{n.phone ? ` · ${n.phone}` : ''}</b>
              <small>{n.paid
                ? `Active until ${fmtUntil(n.until)} · ${n.daysLeft === 0 ? 'ends today' : `${n.daysLeft} day${n.daysLeft === 1 ? '' : 's'} left`}${n.autoRenew ? ' · renews from your wallet' : ''}`
                : 'Not active — referrals on this number aren’t counted until you pay'}</small>
            </div>
            <div className="rc-plan-act">
              {n.paid && (
                <span className="rc-ar"><span>Auto-renew</span>
                  <button type="button" role="switch" aria-checked={n.autoRenew} aria-label={`Auto-renew for ${n.label}`} className={`csv-switch ${n.autoRenew ? 'is-on' : ''}`} disabled={busy === 'ar' + n.id} onClick={() => renew(n)}><span /></button></span>
              )}
              <button type="button" className={`csv-btn csv-btn-sm ${confirm?.id === n.id ? 'csv-btn-dark' : n.paid ? 'csv-btn-ghost' : 'csv-btn-brand'}`}
                onClick={() => pay(n)} disabled={busy === 'pay' + n.id}>
                {busy === 'pay' + n.id ? <span className="csv-spin" /> : null}
                {confirm?.id === n.id ? `Tap again to pay ${naira(price)}` : n.paid ? 'Add a month' : `Pay ${naira(price)}`}
              </button>
              {confirm?.id === n.id && <button type="button" className="csv-btn csv-btn-ghost csv-btn-sm" onClick={() => setConfirm(null)}>Cancel</button>}
            </div>
          </div>
        );
      })}
      {short && <div className="csv-short"><span>{short}</span><Link className="csv-btn csv-btn-dark csv-btn-sm" to="/wallet">Top up wallet</Link></div>}
    </section>
  );
}